/**
 * ZEN ERP — Global Klavye Kısayolları + Yardım Penceresi
 *
 * Kurallar:
 * - Tek tuşlu kısayollar yalnızca bir yazı alanına YAZILMIYORKEN çalışır
 * - Rol bazlı: kullanıcı erişimi olmayan sayfaya kısayol da götüremez
 * - Ctrl+K: ekrandaki birincil "Kaydet" butonunu tetikler (föyde satırı kaydeder);
 *   Ctrl+S / Ctrl+Enter aynı işi yapar (eski kısayollar); Ctrl+F = arama (05.11)
 * - "?" tuşu yardım penceresini açar; ESC kapatır
 */
import React, { useState, useEffect, useContext } from "react";
import { NavigationCtx, AuthCtx } from "./appContexts";
import { isSaveShortcut, isSearchShortcut, isQuickAddKey } from "./utils/fastEntry";
import { Keyboard, X } from "lucide-react";

// ── Gezinme haritası (Sidebar rolleriyle uyumlu) ──
const NAV_KEYS = {
  "1": { tab: "dashboard", label: "Ana Sayfa", desc: "Kontrol paneli", roles: ["admin", "sales", "production", "dealer"] },
  "2": { tab: "orders", label: "Siparişler", desc: "Satış siparişleri", roles: ["admin", "sales", "production", "dealer"] },
  "3": { tab: "quotes", label: "Teklifler", desc: "Verilen teklifler", roles: ["admin", "sales", "dealer"] },
  "4": { tab: "customers", label: "Müşteriler", desc: "Cari hesaplar", roles: ["admin", "sales", "dealer"] },
  "5": { tab: "invoices", label: "Faturalar", desc: "Satış/Alış/İade", roles: ["admin", "sales"] },
  "6": { tab: "stock", label: "Stok & Kartela", desc: "Stok takibi", roles: ["admin", "sales"] },
  "7": { tab: "accounting", label: "Muhasebe", desc: "Kasa & Ödemeler", roles: ["admin", "sales"] },
  "8": { tab: "crm", label: "CRM", desc: "Potansiyel müşteriler", roles: ["admin", "sales"] },
  "9": { tab: "settings", label: "Ayarlar", desc: "Sistem ayarları", roles: ["admin", "sales", "production"] },
  "0": { tab: "reports", label: "Raporlar", desc: "Satış raporları", roles: ["admin"] },
};

// Kaydet butonu öncelik sırası (görünür olan ilk tetiklenir)
const SAVE_IDS = [
  "quote-save-btn", "order-save-approve", "inv-save", "cust-save", "packing-save",
  "acc-save", "po-save-btn", "appt-save-btn", "kartela-save", "sparepart-save-btn",
  "invoice-editor-save", "foy-save-btn", "supplier-save-btn", "workshop-save-btn",
  "assignment-save-btn", "db-backup-save", "balance-save-btn", "btn-save-config",
];

export function Shortcuts() {
  const { activeTab, setTab } = useContext(NavigationCtx);
  const { user } = useContext(AuthCtx);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const isTyping = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
    };

    const focusSearch = () => {
      const inputs = Array.from(document.querySelectorAll("input[type='text'], input:not([type])"));
      const el = inputs.find(e => e.offsetParent !== null && /ara|search/i.test((e.placeholder || "") + (e.getAttribute("data-testid") || "")));
      if (el) { el.focus(); el.select?.(); return true; }
      return false;
    };

    const triggerSave = () => {
      for (const id of SAVE_IDS) {
        const el = document.querySelector(`[data-testid="${id}"]`);
        if (el && el.offsetParent !== null && !el.disabled) { el.click(); return true; }
      }
      const btn = Array.from(document.querySelectorAll("button.zen-btn-primary"))
        .find(b => b.offsetParent !== null && !b.disabled && /kaydet/i.test(b.textContent || ""));
      if (btn) { btn.click(); return true; }
      return false;
    };

    const goNewOrder = () => {
      setTab("orders");
      setTimeout(() => {
        const btn = document.querySelector('[data-testid="new-order-btn"]');
        if (btn) btn.click();
      }, 400);
    };

    const goNewQuote = () => {
      setTab("quotes");
      setTimeout(() => {
        const btn = document.querySelector('[data-testid="quotes-new-btn"]');
        if (btn) btn.click();
      }, 400);
    };

    const onKey = (e) => {
      // Ctrl / Cmd kombinasyonları (05.11: Ctrl+F = arama, Ctrl+K = kaydet —
      // kelimenin ilk harfi kuralı; Ctrl+S / Ctrl+Enter eski kısayollar korunur)
      if (e.ctrlKey || e.metaKey) {
        // Föyde Ctrl+K satırı kaydeder (triggerQuickAdd olayı yutar); buraya
        // düşerse genel kaydettir — ama çift tetiklemeye karşı yine de kontrol.
        if (isQuickAddKey(e, e.target)) return;
        if (isSearchShortcut(e)) { e.preventDefault(); focusSearch(); return; }
        if (isSaveShortcut(e)) { e.preventDefault(); triggerSave(); return; }
        return;
      }
      // ESC — yardım penceresini kapat (diğer ESC davranışları kendi sayfalarında çalışır)
      if (e.key === "Escape") {
        if (helpOpen) { e.preventDefault(); e.stopPropagation(); setHelpOpen(false); }
        return;
      }
      // Yazı alanındayken tek tuşlu kısayollar KAPALI
      if (isTyping(e.target)) return;

      // Yardım penceresi açıkken diğer kısayollar kapalı
      if (helpOpen) return;

      // Gezinme: 0-9
      if (NAV_KEYS[e.key]) {
        const item = NAV_KEYS[e.key];
        if (!user || !item.roles.includes(user.role)) return;
        e.preventDefault();
        setTab(item.tab);
        return;
      }
      // Eylemler
      if (e.key === "/") { e.preventDefault(); focusSearch(); return; }
      if (e.key === "?") { e.preventDefault(); setHelpOpen(true); return; }
      if (e.key.toLowerCase() === "n") { e.preventDefault(); goNewOrder(); return; }
      if (e.key.toLowerCase() === "t") { e.preventDefault(); goNewQuote(); return; }
    };

    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [setTab, user, helpOpen]);

  if (!helpOpen) return null;

  const NavRow = ({ k, item }) => (
    <tr className="border-b border-slate-100 dark:border-[#22304A]">
      <td className="py-1.5 pr-3"><kbd className="px-2 py-0.5 rounded bg-slate-100 dark:bg-[#1A2340] border border-slate-300 dark:border-[#22304A] text-xs font-bold">{k}</kbd></td>
      <td className="py-1.5 pr-3 text-sm font-semibold">{item.label}</td>
      <td className="py-1.5 text-xs text-slate-500">{item.desc}</td>
    </tr>
  );

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="shortcuts-help" onClick={() => setHelpOpen(false)}>
      <div className="zen-panel zen-hc-surface w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black flex items-center gap-2"><Keyboard className="w-5 h-5" /> Klavye Kısayolları</h2>
          <button onClick={() => setHelpOpen(false)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-[#22304A]" data-testid="shortcuts-close"><X className="w-5 h-5" /></button>
        </div>

        <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Sayfa Gezintisi</h3>
        <table className="w-full mb-5">
          <tbody>{Object.entries(NAV_KEYS).map(([k, item]) => <NavRow key={k} k={k} item={item} />)}</tbody>
        </table>

        <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Hızlı Eylemler</h3>
        <table className="w-full mb-5">
          <tbody>
            {[
              ["N", "Yeni Sipariş", "Sipariş sayfasını açar ve yeni sipariş formunu başlatır"],
              ["T", "Yeni Teklif", "Teklif oluşturucuyu açar"],
              ["/", "Hızlı Arama", "Açık sayfadaki arama kutusuna odaklanır"],
              ["?", "Bu pencere", "Kısayol listesini gösterir"],
            ].map(([k, label, desc]) => (
              <tr key={k} className="border-b border-slate-100 dark:border-[#22304A]">
                <td className="py-1.5 pr-3 w-16"><kbd className="px-2 py-0.5 rounded bg-slate-100 dark:bg-[#1A2340] border border-slate-300 dark:border-[#22304A] text-xs font-bold">{k}</kbd></td>
                <td className="py-1.5 pr-3 text-sm font-semibold">{label}</td>
                <td className="py-1.5 text-xs text-slate-500">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Ctrl Kombinasyonları (her yerde çalışır)</h3>
        <table className="w-full">
          <tbody>
            {[
              ["Ctrl+K", "Kaydet", "Açık formun/föyün birincil Kaydet butonunu tetikler; ürün föyündeyken satırı sepete kaydeder ve yeni kaleme başlar"],
              ["Ctrl+S", "Kaydet (alternatif)", "Ctrl+K ile aynı"],
              ["Ctrl+Enter", "Kaydet (alternatif)", "Ctrl+K ile aynı"],
              ["Ctrl+F", "Hızlı Arama", "Arama kutusuna odaklanır"],
              ["Ctrl+D", "Kalemi Çoğalt", "Son kalemi kopyalar (ürün föyündeyken de çalışır); sepetteki ⧉ düğmesi aynı işi yapar"],
            ].map(([k, label, desc]) => (
              <tr key={k} className="border-b border-slate-100 dark:border-[#22304A] last:border-0">
                <td className="py-1.5 pr-3 w-24"><kbd className="px-2 py-0.5 rounded bg-slate-100 dark:bg-[#1A2340] border border-slate-300 dark:border-[#22304A] text-xs font-bold">{k}</kbd></td>
                <td className="py-1.5 pr-3 text-sm font-semibold">{label}</td>
                <td className="py-1.5 text-xs text-slate-500">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-5 text-[11px] text-slate-400 leading-relaxed">
          💡 Tek tuşlu kısayollar bir yazı alanına <b>yazarken çalışmaz</b> — yazdığınız "n" harfi sipariş açmaz.
          ESC bu pencereyi kapatır. Herhangi bir ekranda <kbd className="px-1 rounded bg-slate-100 dark:bg-[#1A2340]">?</kbd> tuşuna basarak bu listeyi yeniden açabilirsiniz.
        </div>
      </div>
    </div>
  );
}
